
import { Options, prop, Vue } from "vue-class-component";
import OverlayPanel from "primevue/overlaypanel";
import {
  SavedPackageBookingV4,
  SavedPackagePieceBookingV4,
} from "@/domain/entities/BookingV4";
import { BookingV4Controller } from "@/app/ui/controllers/BookingV4Controller";

interface SavedPackageDetailCache {
  pieces: SavedPackagePieceBookingV4[];
  totalGrossWeight: number;
}

const detailCache = new Map<number, SavedPackageDetailCache>();

class Props {
  pkg = prop<SavedPackageBookingV4>({
    required: true,
    default: () => new SavedPackageBookingV4(),
  });
  customerId = prop<number>({
    required: true,
    default: 0,
  });
  displayName = prop<(item: SavedPackageBookingV4) => string>({
    required: true,
    default: () => (_item: SavedPackageBookingV4) => "",
  });
}

@Options({
  name: "CommodityTooltip",
  components: {
    OverlayPanel,
  },
})
class CommodityTooltip extends Vue.with(Props) {
  loading = false;
  detailPieces: SavedPackagePieceBookingV4[] = [];
  totalGrossWeight = 0;

  toggle(event: any) {
    const overlay: any = this.$refs.overlay;
    overlay?.toggle(event);

    if (!this.pkg.id) return;

    const cached = detailCache.get(this.pkg.id);
    if (cached) {
      this.detailPieces = cached.pieces;
      this.totalGrossWeight = cached.totalGrossWeight;
      this.reposition();
    } else {
      this.fetchDetail();
    }
  }

  hide(event?: any) {
    const overlay: any = this.$refs.overlay;
    overlay?.hide(event);
  }

  reposition() {
    setTimeout(() => {
      const el = (this.$refs.overlay as any)?.container;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      if (rect.bottom > viewportHeight) {
        const overflow = rect.bottom - viewportHeight + 8;
        const currentTop = parseFloat(el.style.top) || 0;
        el.style.top = `${currentTop - overflow}px`;
      } else if (rect.top < 0) {
        const overflow = -rect.top + 8;
        const currentTop = parseFloat(el.style.top) || 0;
        el.style.top = `${currentTop + overflow}px`;
      }
    }, 200);
  }

  async fetchDetail() {
    if (!this.customerId || !this.pkg.id) return;

    this.loading = true;
    try {
      const detail = await BookingV4Controller.getSavedPackageDetail({
        customerId: this.customerId,
        packageId: this.pkg.id
      });
      const pieces = detail.pieces || [];
      const totalGrossWeight =
        pieces.reduce((sum, p) => sum + (p.grossWeight || 0), 0) ||
        this.pkg.totalGrossWeight || 0;
      detailCache.set(this.pkg.id, { pieces, totalGrossWeight });
      this.detailPieces = pieces;
      this.totalGrossWeight = totalGrossWeight;
    } catch {
      this.detailPieces = [];
      this.totalGrossWeight = this.pkg.totalGrossWeight || 0;
    } finally {
      this.loading = false;
      this.reposition();
    }
  }
}

export default CommodityTooltip;
